import get from 'lodash/get'
import isString from 'lodash/isString'
import isUndefined from 'lodash/isUndefined'
import memoize from 'lodash/memoize'
import { v4 as uuidv4 } from 'uuid'
import { transformCalculatedField } from '@utils/calculated-transformation-utils'
import * as ListDataManager from 'dataContainer/listDataManager'
import { generateDAG } from './dag-generator'
import { transformLink } from './transformers/link.transformers'
import { getValueTransformer } from './transformers/transformers'
import * as Validations from './validators'
import {
  getCalculatedFromTemplateField,
  hasValidOptions,
  isTemplateFieldListOfType,
  isGovernanceType,
  isFieldGovernanceType,
} from '../field-governance/calculated/utils'

export class DataContainer {
  constructor(templateFields = [], options = {}, pendingRows = new Set(), comments = {}) {
    this.datasetUuid = options.datasetUuid || null
    this.data = []
    this.dataset = []
    this.listeners = {}
    this.options = options
    this.initializedRows = []
    this.pendingRows = pendingRows
    this.comments = comments
    this.changeCallback = () => {}
    this.forceHOTRender = () => {}
    this.setTemplateFields(templateFields)
    this.uniqueInstanceId = uuidv4()
    this.fieldsUpdatedAfterLoad = []
  }

  forceRender() {
    this.forceHOTRender()
  }

  getUniqueInstanceId() {
    return this.uniqueInstanceId
  }

  getNonEmptyRows() {
    return this.data.filter((row, index) => !this.isEmptyRow(index))
  }

  buildDatasetRowsFromData() {
    const data = this.getNonEmptyRows()
    return data.map(row => ({
      cvid: row.cvid || '',
      dataset_values: Object.values(row).map(cell => ({
        field_id: String(cell.field_id),
        cell_value: cell.cell_value,
      })),
    }))
  }

  isEmptyRow(rowNum) {
    const IGNORED_GOVERNANCE_TYPES = {
      constant: true,
      concatenation: true,
      autofill: true,
      'link-validation': true,
    }

    let isEmpty = true

    Object.values(this.templateFieldsByFieldId).forEach(col => {
      const value = this.data[rowNum][col.field_id] && this.data[rowNum][col.field_id].cell_value
      if (value !== '' && value !== null && !isUndefined(value)) {
        // value is set, so lookup the internals
        // this works because the metadata base is the template_field
        if (
          IGNORED_GOVERNANCE_TYPES[col.governance_type] ||
          IGNORED_GOVERNANCE_TYPES[col.field.governance_type] ||
          col.default_value ||
          this.data[rowNum][col.field_id].metadata.autoPopulated
        ) {
          // easier to read this way logically. Niether it's tempalte field or field have bad govnernace types.
        } else {
          isEmpty = false
        }
      }
    })

    return isEmpty
  }

  getDataForRowAndColumn(row, column) {
    const cellObj = this.data[row][column.field_id] || {}
    return cellObj.cell_value
  }

  getValueHolderForRowAndColumn(row, column) {
    return this.data[row][column.field_id] || {}
  }

  setTemplateFields(templateFields) {
    this.templateFields = JSON.parse(JSON.stringify(templateFields))

    this.defaultListValues = this.templateFields.filter(tf => {
      if (!get(tf, 'field.list_governance')) {
        return false
      }
      return !!tf.default_value || !!tf.default_list_value_id
    })

    this.constantTemplateFields = this.templateFields.filter(isConstantValueTemplateField)

    appendListDependents(this.templateFields)

    this.templateFieldsByFieldId = this.templateFields.reduce((memo, templateField) => {
      memo[templateField.field_id] = templateField
      return memo
    }, {})

    this.dag = generateDAG(this.templateFields)
    this.dataSchema = templateRowSchema(this.templateFields)
    this.touchLists()
  }

  getDataSchema() {
    return this.dataSchema
  }

  /**
   * This is meant to be called when, for any reason, the data schema needs to be updated
   * and/or set again. One use-case is on resubmit-dataset, we need to call this because
   * after loading the pending rows, we reset the data container.
   * @param {HandsOnTableBinder} binderRef
   * @param {*} dataset
   * @param {(this) => void} afterUpdateCallback
   */
  updateDatasetData(binderRef, dataset, afterUpdateCallback) {
    const allRenderedColumns = binderRef?.current?.buildAllRenderColumns?.()
    this.allRenderColumns = allRenderedColumns
    this.loadDataFromDataset(dataset)
    afterUpdateCallback?.(this)
  }

  /**
   * Whenever an updated dataset is available, we must
   * update the data container with the new data.
   * - Note: For shared drafts to query the latest data when navigating to a draft,
   * we must not include the commitRef in the query options.
   * @param dc Data Container instance.
   * @param updatedDataset New transformed dataset. Fetched version of the dataset
   * does not have the dataset_rows key.
   * @returns
   */
  updateDatasetDataAndTemplateFields({
    binderRef,
    dataset,
    transformedColumns,
    afterUpdateCallback,
  }) {
    if (!binderRef.current || !transformedColumns) return

    /**
     * Why is there a possibility for the dataContainer templateFields to be different from transformedColumns?
     * - FIXME: Maybe setTemplateFields does not need to be called?
     */
    this.setTemplateFields(transformedColumns)
    this.updateDatasetData(binderRef, dataset, afterUpdateCallback)
  }

  buildDatasetRow = datasetRow => {
    const { dataset_values, cvid: rowCvid } = datasetRow
    const serverRow = this.buildDatasetValues(dataset_values, rowCvid)
    const emptyRow = JSON.parse(JSON.stringify(this.dataSchema))
    return { ...emptyRow, ...serverRow }
  }

  buildDatasetValues = (values, rowCvid) => {
    return values.reduce((memo, value) => {
      const { field_id, cell_value, isClassificationColumn = false, errorCorrection } = value
      const cvid = rowCvid || value?.cvid
      const metadata = this.mergeMetadata(field_id, cvid, cell_value)

      memo[field_id] = {
        cvid,
        cell_value,
        field_id,
        originalValue: this.options.resubmit ? cell_value : null,
        warnings: [],
        validationErrors: [],
        source: [],
        readOnly: false,
        isClassificationColumn,
        metadata,
      }

      if (errorCorrection) {
        memo[field_id].errorCorrection = errorCorrection
      }

      return memo
    }, {})
  }

  mergeMetadata(fieldId, cvid, cellValue) {
    const existingMetadata = this.dataSchema[fieldId]?.metadata || {}
    const updatedMetadata = {
      ...existingMetadata,
      readOnly: existingMetadata?.readOnly,
    }

    if (this.comments?.[cvid]?.[fieldId]) {
      const comment = this.comments[cvid][fieldId]
      updatedMetadata.comment = comment
      updatedMetadata.originalComment = comment
      updatedMetadata.rejectedValue = cellValue
    }

    return updatedMetadata
  }

  recalculateAllCells() {
    this.changes = this.data.flatMap((row, rowNum) => {
      Validations.validateRow(rowNum, this.allRenderColumns, this, this.allRenderColumns)
      this.initializedRows[rowNum] = true

      return Object.entries(row).map(([fieldId, field]) => {
        const currentValue = field.cell_value
        field.metadata = { ...field.metadata, row: rowNum }
        return this.recordChange(rowNum, fieldId, currentValue, null, { init: true })
      })
    })
  }

  loadDataFromDataset(dataset) {
    this.dataset = dataset

    const processedData = dataset.dataset_rows.map(this.buildDatasetRow)

    // Maintain the same reference to data by clearing the array and pushing
    // the processed data into it.
    this.data.length = 0
    this.data.push(...processedData)

    this.recalculateAllCells()

    this.fireHook('afterLoadData', this.changes)
    this.changeCallback()
  }

  getFieldVisibility(fieldId) {
    return this.templateFieldsByFieldId[fieldId].visible_on_form
  }

  changeFieldVisibility(fieldId, visible) {
    this.templateFields = this.templateFields.map(tf => {
      if (tf.field_id == fieldId)
        return {
          ...tf,
          visible_on_form: visible,
        }

      return tf
    })

    this.templateFieldsByFieldId[fieldId].visible_on_form = visible
    this.fieldsUpdatedAfterLoad.push(fieldId)
  }

  addHook(hookName, fn) {
    if (!this.listeners[hookName]) {
      this.listeners[hookName] = []
    }

    this.listeners[hookName].push(fn)
  }

  fireHook(hookName, params) {
    const listeners = this.listeners[hookName] || []
    listeners.forEach(toCall => {
      toCall(params)
    })
  }

  initializeRow(rowNum) {
    this.initializedRows[rowNum] = true
    this.touchConstants(rowNum)
    this.touchLists(rowNum)

    let initChanges = []
    Object.values(this.templateFieldsByFieldId).forEach(col => {
      if (col.default_value) {
        initChanges = [
          ...initChanges,
          ...this.recordChange(rowNum, col.field_id, col.default_value, null, {}),
        ]
      }
    })
    return initChanges
  }

  unitializeRow(rowNum) {
    this.initializedRows[rowNum] = null
  }

  getLinkValidationColumn() {
    return Object.values(this.allRenderColumns).find(
      col =>
        isGovernanceType(col, 'link-validation') || isFieldGovernanceType(col, 'link-validation'),
    )
  }

  /**
   * Mutates through all transformations and validations for a given field, including dependent
   * fields.  This function is the primary entry point for all data changes.
   * - A dependent field is a field that is calculated from the current field.
   * @param {*} rowNum
   * @param {*} fieldId
   * @param {*} newValue
   * @param {*} oldValue
   * @param {*} context
   * @returns An array of applied changes.
   */
  recordChange(rowNum, fieldId, newValue, oldValue, context = {}) {
    if (!this.dag) {
      throw Error('[CLARAVINE] DataContainer.recordChange called before DAG initialized')
    }

    let appliedChanges = []

    // we need to track when something was last deleted. So that drill downs don't re-autofill in a row when you hit delete.
    if (!newValue && oldValue) {
      context.last_deleted = Date.now()
    }

    const trigger = { rowNum, fieldId, newValue, oldValue }
    const internalContext = { trigger, ...context, root: false }

    const templateField = this.templateFieldsByFieldId[fieldId]

    // run the validators here instead of on render.
    const rowObj = this.data[rowNum] || {}
    const valueObj = rowObj[fieldId] || {}

    // Run transformations on current column/cell (parent of dependent changes)
    // Why is this necessary? Shouldn't we validate the cell first to prevent transforms on invalid cells.
    const parentChange = this.updateCellValue(rowNum, fieldId, newValue, oldValue, internalContext)
    appliedChanges.push(parentChange)

    // recalculate the states of the descendants
    const memoizedGetAllDescendants = memoize(this.dag.getAllDescendants.bind(this.dag))
    const dependentFields = memoizedGetAllDescendants(fieldId)

    for (const descendantTemplateField of dependentFields) {
      const dependentFieldId = descendantTemplateField.field_id
      const depValueObj = this.data[rowNum][dependentFieldId] || {}
      const desContext = { ...internalContext }
      const calculated = getCalculatedFromTemplateField(descendantTemplateField)

      depValueObj.metadata.row = rowNum
      desContext.parentOriginalValue = valueObj.originalValue
      desContext.parentCurrentValue = valueObj.cell_value

      let currentValue = depValueObj.cell_value
      let textSplitValidation = false

      if (calculated) {
        const splitTextCurrentCalculation =
          descendantTemplateField.field?.transformation?.calculated.filter(
            c => c.origin_field_id == fieldId,
          )[0]

        if (splitTextCurrentCalculation) {
          const allowReprocessing = splitTextCurrentCalculation.allow_reprocessing
          if (allowReprocessing || (!allowReprocessing && (currentValue == '' || !currentValue))) {
            currentValue = transformCalculatedField(
              parentChange.newValue,
              splitTextCurrentCalculation,
            )
            textSplitValidation = true
          }
        }
      }

      const dependentChange = this.updateCellValue(
        rowNum,
        dependentFieldId,
        currentValue,
        currentValue,
        desContext,
      )

      if (textSplitValidation) {
        // Validate the new cell we are splitting to.
        Validations.validateCell(
          descendantTemplateField,
          rowNum,
          dependentFieldId,
          this.templateFieldsByFieldId,
          [], // passing "dependentFields" as an empty array here because it was messing with validations (CUST-2714), and on line 288 we call this function recursively
          depValueObj,
          this.data,
          this,
        )

        // If there is a text split validation performed, we need to call recordChange on the fieldID that was transformed to account for chaining multiple split text operations
        this.recordChange(rowNum, dependentChange.fieldId, currentValue, currentValue, context)
      }

      if (isTemplateFieldListOfType(descendantTemplateField, 'drill-down')) {
        Validations.validateCell(
          descendantTemplateField,
          rowNum,
          dependentFieldId,
          this.templateFieldsByFieldId,
          dependentFields,
          depValueObj,
          this.data,
          this,
        )
      }

      if (isGovernanceType(descendantTemplateField, 'link')) {
        const linkValidationColumn = this.getLinkValidationColumn()
        const newValueObj = {
          metadata: {
            link_governance: descendantTemplateField.field.link_governance,
            linkField: descendantTemplateField,
          },
          cell_value: dependentChange.newValue,
        }

        if (linkValidationColumn && !context.init) {
          Validations.validateCell(
            linkValidationColumn,
            rowNum,
            linkValidationColumn.field_id,
            this.allRenderColumns,
            null,
            newValueObj,
            this.data,
            this,
          )
        }
      }

      appliedChanges.push(dependentChange)
    }

    // The second validation is for the case when a drilldown value was wrong and consequently set as null, so
    // the cell was not being validated.
    if (
      oldValue !== newValue ||
      (isFieldGovernanceType(templateField, 'list') &&
        newValue === null &&
        hasValidOptions(valueObj))
    ) {
      Validations.validateCell(
        templateField,
        rowNum,
        fieldId,
        this.templateFieldsByFieldId,
        dependentFields,
        valueObj,
        this.data,
        this,
      )
      Validations.validateRow(rowNum, this.allRenderColumns, this, this.allRenderColumns)
      if (valueObj?.metadata?.rejectedValue !== null) {
        updateComments({ newCellValue: newValue, cellData: valueObj })
      }
    }

    if (
      templateField &&
      (templateField.governance_type === 'link' ||
        (templateField.field.governance_type === 'link' && newValue))
    ) {
      const linkValidationColumn = this.getLinkValidationColumn()

      valueObj.metadata.link_governance = templateField.field?.link_governance
      valueObj.metadata.linkField = templateField

      if (linkValidationColumn && newValue && !context.init) {
        Validations.validateCell(
          linkValidationColumn,
          rowNum,
          linkValidationColumn.field_id,
          this.allRenderColumns,
          null,
          valueObj,
          this.data,
          this,
        )
      }
    }

    // this won't work with multiple deletes. It'll just unit-, and then reinit.
    if ((!newValue && oldValue) || (!newValue && !oldValue)) {
      // check to see if row is now emtpy.

      if (this.isEmptyRow(rowNum)) {
        this.unitializeRow(rowNum)

        if (valueObj && valueObj.metadata) {
          valueObj.metadata.validationErrors = []
        }
      }
    } else if (context.root && !this.initializedRows[rowNum]) {
      appliedChanges = [...appliedChanges, ...this.initializeRow(rowNum)]
    }

    // Allow changeCallback to be skipped, and changes are present;
    // Ideal for calling once outisde this function, after all record changes.
    // Otherwise, this can create a performance bottleneck; e.g. copy paste.
    if (context?.batch === false && appliedChanges.length > 0) {
      this.changeCallback()
    }

    return appliedChanges
  }

  updateCellValue(rowNum, fieldId, newValue, oldValue, context) {
    let row = this.data[rowNum]

    // if no row exists, then we need to create it
    if (!row) {
      this.data[rowNum] = JSON.parse(JSON.stringify(this.dataSchema))
      row = this.data[rowNum]
    }

    if (oldValue && !newValue) {
      context.last_deleted = Date.now()
    }

    const templateField = this.templateFieldsByFieldId[fieldId]
    const transformer = getValueTransformer(templateField, {
      disableTransformers: this.options.disableTransformers,
      runReadOnlyTransformers: this.options.runReadOnlyTransformers,
    })

    const valueHolder = row[fieldId] || {}
    const trimmed = isString(newValue) ? newValue.trim() : newValue
    valueHolder.cell_value = trimmed
    context.dataContainer = this
    context.originalValue = this.options.resubmit ? valueHolder.originalValue : null
    context.rowNum = rowNum
    context.isResubmit = this.options.resubmit

    let transformedValue = transformer(
      valueHolder,
      row,
      templateField,
      this.templateFields,
      context,
      this,
      this.options.runReadOnlyTransformers,
    )

    if (templateField && templateField.governance_type === 'link' && templateField.autofill) {
      valueHolder.cell_value = transformedValue
      transformedValue = transformLink(
        valueHolder,
        row,
        templateField,
        this.templateFields,
        context,
      )
    }

    valueHolder.cell_value = transformedValue
    if (!valueHolder.metadata) {
      valueHolder.metadata = {}
    }
    valueHolder.metadata.row = rowNum

    return {
      rowNum,
      fieldId,
      newValue: valueHolder.cell_value,
      oldValue,
      originalValue: this.options.resubmit ? context.originalValue : null,
    }
  }

  /**
   * Since constants can never have user input, we need to trigger
   * any updates on constant cells at least once.  This ensures that
   * things like concat/autofill of constants are set, even if the
   * only inputs are constants.
   *
   * @param rowNum
   */
  touchLists() {
    this.defaultListValues.forEach(templateFieldWithDefault => {
      const descendants =
        this.dag.descendantTemplateFieldsByFieldId[templateFieldWithDefault.field_id]

      if (descendants && descendants.length) {
        // Next child should be determined by being the next list column.
        // Just grabbing the next descedenant is problematic; if the next item
        // was a concatenation field, the filtered value would be lost and not
        // applied to the correct column.
        const nextChild = descendants.find(field => field?.field?.governance_type === 'list')

        const col = Object.values(this.templateFieldsByFieldId).find(col => {
          return col.field_id === nextChild?.field_id
        })

        const listGovernance = templateFieldWithDefault.field.list_governance
        const listId = listGovernance.list_id
        const listColumnId = listGovernance.list_column_id
        const listData = ListDataManager.getListData(listId)
        const columnListValuesForDefault = listData.listValuesPerColumn[listColumnId]
        const matchingRows = columnListValuesForDefault
          .filter(lv => {
            return lv.value === templateFieldWithDefault.default_value
          })
          .reduce((memo, lv) => {
            memo[lv.row_number] = true
            return memo
          }, {})

        // override the set of valid values for the child
        const governanceOwner = col?.field?.segment_type === 'variable' ? col : col?.field
        const colListGovernance = governanceOwner?.list_governance
        if (colListGovernance) {
          const colListId = colListGovernance.list_id
          const colListColumnId = colListGovernance.list_column_id
          const colListData = ListDataManager.getListData(colListId)
          const colListValues = colListData.listValuesPerColumn[colListColumnId]
          const filter = colListValues.filter(vals => {
            return matchingRows[vals.row_number]
          })
          ListDataManager.overrideListValuesPerColumn(colListId, colListColumnId, filter)
        }
      }
    })
  }

  touchConstants(rowNum) {
    // we generate a single marker per batch so that we can compare
    // to determine if we touched them in this pass or previous passes
    const touchedMarker = new Date()

    this.defaultListValues.forEach(tf => {
      const constantFieldId = tf.field_id
      const rowObj = this.data[rowNum] || {}
      const valueObj = rowObj[constantFieldId] || {}
      if (!valueObj.metadata) {
        valueObj.metadata = {}
      }
      if (!valueObj.metadata.touched) {
        valueObj.metadata.touched = touchedMarker
      }
    })

    this.constantTemplateFields.forEach(tf => {
      const constantFieldId = tf.field_id
      const rowObj = this.data[rowNum] || {}
      const valueObj = rowObj[constantFieldId] || {}
      if (!valueObj.metadata) {
        valueObj.metadata = {}
      }
      if (!valueObj.metadata.touched) {
        valueObj.metadata.touched = touchedMarker
        this.recordChange(rowNum, tf.field_id)
      }
    })

    //--------------------------------------------------------------------
    // now that everything has been marked as touched, actually trigger
    // the recordChange functions.  Doing this in two phases ensures that
    // we don't cause deeply nested recursion (with overflows)
    //--------------------------------------------------------------------

    this.defaultListValues.forEach(tf => {
      const constantFieldId = tf.field_id
      const rowObj = this.data[rowNum] || {}
      const valueObj = rowObj[constantFieldId] || {}
      const metadata = valueObj.metadata || {}
      if (metadata.touched === touchedMarker) {
        this.recordChange(rowNum, tf.field_id, tf.default_value)
      }
    })

    this.constantTemplateFields.forEach(tf => {
      const constantFieldId = tf.field_id
      const rowObj = this.data[rowNum] || {}
      const valueObj = rowObj[constantFieldId] || {}
      const metadata = valueObj.metadata || {}
      if (metadata.touched === touchedMarker) {
        this.recordChange(rowNum, tf.field_id)
      }
    })
  }
}

function templateRowSchema(templateColumns) {
  const rowSchema = templateColumns.reduce((memo, column) => {
    const defaultCellValue = getDefaultCellValue(column)
    memo[column.field_id] = {
      cell_value: defaultCellValue,
      field_id: column.field_id,
      metadata: {
        warnings: [],
        validationErrors: [],
        isVisible: column.visible_on_form,
        readOnly: !!defaultCellValue || column.readOnly || Boolean(column.field.read_only),
        source: [],
        row: null,
      },
    }
    return memo
  }, {})

  return rowSchema
}

function getDefaultCellValue(templateField) {
  const governanceOwner =
    templateField.field.segment_type === 'variable' ? templateField : templateField.field

  const governanceType = governanceOwner.governance_type
  return governanceType === 'constant' ? governanceOwner.constant_value : null
}

// TODO: ALL OF THIS CAN BE REMOVED ONCE WE'VE UPDATED THE TRANSFORMERS TO THE NEW VERSIONS

function appendListDependents(templateColumns) {
  const columnsByListId = getColumnsByListId(templateColumns)
  const nextChildrenByColumnDataId = getNextChildren(templateColumns, columnsByListId)
  const defaultValueLists = templateColumns.reduce((memo, tc) => {
    if (tc.field && tc.field.list_governance && tc.default_value) {
      memo[tc.field.id] = true
    }
    return memo
  }, {})

  const depsByListId = getDependencies(columnsByListId)

  // for each list, update the template columns with their dependencies
  templateColumns.forEach(column => {
    const gov = column.field.segment_type === 'variable' ? column : column.field
    const listGovernance = gov.list_governance
    if (listGovernance) {
      const list = listGovernance.lists || {}
      const { type } = list
      listGovernance.meta = listGovernance.meta || {}
      if (type !== 'drop-down') {
        listGovernance.meta.allColumnsInList = columnsByListId[listGovernance.list_id]
      } else {
        // if a drop down is used twice in a list with different governance (multi vs single select) things get dumb
        listGovernance.meta.allColumnsInList = [column]
      }

      // certain properties only exist on drill down columns
      if (type === 'drill-down') {
        listGovernance.meta.nextChildren = nextChildrenByColumnDataId[column.field_id]
        listGovernance.meta.allListDependencies = depsByListId[listGovernance.list_id]
        listGovernance.meta.predecessors =
          (listGovernance.meta.allListDependencies &&
            listGovernance.meta.allListDependencies[column.field_id]) ||
          []
      }
    }
  })

  // for each column, update the default read only state if it's a drill-down
  // special check to enable
  templateColumns.forEach(column => {
    const gov = column.field.segment_type === 'variable' ? column : column.field
    const { list_governance } = gov
    const meta = list_governance ? list_governance.meta : null
    const list = list_governance ? list_governance.lists : null
    const type = list ? list.type : null
    if (column.governance_type === 'link' && column.autofill) {
      column.readOnly = true
    }
    if (meta && type === 'drill-down') {
      const checkPredecessors = meta.predecessors.filter(pred => {
        return !defaultValueLists[pred]
      })
      const hasPredecessors = !!(checkPredecessors || []).length
      column.readOnly = hasPredecessors
    }
  })
}

function orderByColumnPrecedenceAsc(a, b) {
  const aGov = a.field.segment_type === 'variable' ? a : a.field
  const bGov = b.field.segment_type === 'variable' ? b : b.field
  const aListColumn = aGov.list_governance && aGov.list_governance.list_column
  const bListColumn = bGov.list_governance && bGov.list_governance.list_column
  const aPrecedence = aListColumn ? aListColumn.precedence : 0
  const bPrecedence = bListColumn ? bListColumn.precedence : 0
  return aPrecedence - bPrecedence
}

function getColumnsByListId(templateColumns) {
  //
  const columnsByListId = templateColumns.reduce((memo, column) => {
    const gov = column.field.segment_type === 'variable' ? column : column.field
    const listGovernance = gov.list_governance
    if (listGovernance) {
      const listId = listGovernance.list_id
      if (!memo[listId]) {
        memo[listId] = []
      }
      memo[listId].push(column)
    }
    return memo
  }, {})

  // sort each of the column lists
  Object.values(columnsByListId).forEach(columnsInList => {
    columnsInList.sort(orderByColumnPrecedenceAsc)
  })

  return columnsByListId
}

// todo: I wrote this and still don't understand it.
// todo: add some clarifying comments
function getNextChildren(templateColumns, columnsByListId) {
  const nextChildrenByFieldId = templateColumns.reduce((memo, column) => {
    const gov = column.field.segment_type === 'variable' ? column : column.field
    const listGovernance = gov.list_governance
    if (listGovernance) {
      const listId = listGovernance.list_id
      const columnsInList = columnsByListId[listId]
      const listColumn = listGovernance.list_column
      if (!listColumn) {
        console.warn('[CLARAVINE] Missing list_column for templateField', column.id)
      }
      const currentPrecedence = listColumn ? listColumn.precedence : 0
      const nextPrecedence = columnsInList.reduce((memo, column) => {
        const gov = column.field.segment_type === 'variable' ? column : column.field
        const precedenceIter =
          (gov.list_governance &&
            gov.list_governance.list_column &&
            gov.list_governance.list_column.precedence) ||
          0
        return memo === null && currentPrecedence < precedenceIter ? precedenceIter : memo
      }, null)
      const nextChildren = columnsInList.filter(column => {
        const gov = column.field.segment_type === 'variable' ? column : column.field
        const precedence =
          (gov.list_governance &&
            gov.list_governance.list_column &&
            gov.list_governance.list_column.precedence) ||
          0
        return (
          nextPrecedence !== null && nextPrecedence !== undefined && nextPrecedence <= precedence
        )
      })
      memo[column.field_id] = nextChildren
    }

    return memo
  }, {})
  return nextChildrenByFieldId
}

function getDependencies(columnsByListId) {
  // map of list id -> { columnDataId: deps, ... }
  const byListId = Object.keys(columnsByListId).reduce((memo, listId) => {
    const columnsInList = columnsByListId[listId]
    const deps = columnsInList.reduce(
      (memo, column) => {
        memo[column.field_id] = [...memo._iter]
        memo._iter.push(column.field_id)
        return memo
      },
      { _iter: [] },
    )
    delete deps._iter
    memo[listId] = deps
    return memo
  }, {})

  return byListId
}

function isConstantValueTemplateField(templateField = {}) {
  const field = templateField.field || {}
  const governanceOwner = field.segment_type === 'variable' ? templateField : field

  const governanceType = governanceOwner.governance_type
  return governanceType === 'constant'
}

function updateComments({ newCellValue, cellData }) {
  if (newCellValue && newCellValue !== cellData?.metadata?.rejectedValue) {
    const cellHasComment = Boolean(cellData?.metadata?.comment)

    if (cellHasComment) {
      delete cellData.metadata.comment
    }
  } else {
    cellData.metadata.comment = cellData.metadata?.originalComment
  }
}
